// Vollea — Shareable Winner Card (9:16 vertical, IG-stories friendly).
// Two variants:
//   • variant="league"  → top of the league/division standings (winner team or player)
//   • variant="player"  → personal achievement card (rank, points, win-rate, recent form)
//
// Renders at a fixed 1080×1920 internal size so the exported PNG is always
// high-resolution regardless of how the modal is scaled on screen. The
// wrapping <div ref={cardRef}> is what ShareCardModal hands to html-to-image.
import React from "react";
import { IoTrophy, IoSparkles, IoTrendingUp, IoFlame } from "react-icons/io5";
import { C } from "../lib/theme";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vollea-match/artifacts/bbkllrr6_113445FF-96D8-45A9-AAF7-E1BF1221DD62.png";

// Fixed render box — html-to-image captures this size and we let CSS scale
// the preview down to fit the modal. crossOrigin so the logo doesn't taint
// the canvas during PNG export.
const CARD_W = 1080;
const CARD_H = 1920;

export const CARD_DIMENSIONS = { width: CARD_W, height: CARD_H };

const initials = (name) => (name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

function Avatar({ uri, name, size }) {
  const fontSize = Math.round(size * 0.36);
  if (uri) {
    return (
      <img
        src={uri}
        alt=""
        crossOrigin="anonymous"
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `4px solid ${C.lime}` }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: C.surface2, border: `4px solid ${C.lime}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: C.lime, fontWeight: 800, fontSize, letterSpacing: 1,
    }}>{initials(name)}</div>
  );
}

const wrapStyle = {
  width: CARD_W, height: CARD_H,
  background: C.bg,
  // Subtle radial accent in the brand lime so the black card has depth.
  backgroundImage: `radial-gradient(circle at 20% 0%, rgba(168,225,43,0.18) 0%, transparent 55%), radial-gradient(circle at 100% 100%, rgba(168,225,43,0.08) 0%, transparent 60%)`,
  color: C.text,
  position: "relative",
  overflow: "hidden",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  padding: 80,
  display: "flex", flexDirection: "column",
  boxSizing: "border-box",
};

const headerRow = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 60 };
const cornerBadge = {
  display: "inline-flex", alignItems: "center", gap: 14,
  background: C.lime, color: C.onLime,
  padding: "16px 28px", borderRadius: 999,
  fontWeight: 800, fontSize: 28, letterSpacing: 2, textTransform: "uppercase",
};

// ---------------- LEAGUE WINNER CARD ----------------
function LeagueWinnerBody({ leagueName, division, winner, runners, dateLabel }) {
  return (
    <>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div style={{ color: C.lime, fontSize: 36, fontWeight: 700, letterSpacing: 6, textTransform: "uppercase", marginBottom: 16 }}>
          {division ? `Division ${division}` : "League"}
        </div>
        <div style={{ color: C.text, fontSize: 64, fontWeight: 800, lineHeight: 1.05, maxWidth: 880, marginBottom: 80 }}>
          {leagueName}
        </div>

        <div style={{ marginBottom: 36 }}>
          <Avatar uri={winner?.photo} name={winner?.name || "?"} size={280} />
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 18, color: C.lime, fontSize: 36, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", marginBottom: 18 }}>
          <IoTrophy size={42} /> Champion
        </div>
        <div style={{ color: C.text, fontSize: 76, fontWeight: 800, lineHeight: 1.1, maxWidth: 920 }}>
          {winner?.name || "TBC"}
        </div>
        {winner?.points != null && (
          <div style={{ color: C.textMuted, fontSize: 32, marginTop: 28, fontWeight: 600 }}>
            {winner.points} pts · {winner.wins ?? 0}W / {winner.losses ?? 0}L
          </div>
        )}

        {runners && runners.length > 0 && (
          <div style={{ marginTop: 80, width: "100%", maxWidth: 880 }}>
            <div style={{ color: C.textMuted, fontSize: 24, letterSpacing: 4, textTransform: "uppercase", fontWeight: 700, marginBottom: 24, textAlign: "left" }}>Podium</div>
            {runners.map((r, i) => (
              <div key={r.user_id || i} style={{ display: "flex", alignItems: "center", gap: 24, padding: "20px 0", borderBottom: `2px solid ${C.divider}` }}>
                <div style={{ width: 64, color: i === 0 ? C.lime : C.text, fontWeight: 800, fontSize: 40, textAlign: "center" }}>{i + 2}</div>
                <Avatar uri={r.photo} name={r.name} size={72} />
                <div style={{ flex: 1, textAlign: "left", color: C.text, fontWeight: 700, fontSize: 36, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                <div style={{ color: C.lime, fontWeight: 800, fontSize: 36 }}>{r.points ?? 0}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 40 }}>
        <div style={{ color: C.textMuted, fontSize: 24, fontWeight: 600 }}>{dateLabel || "Vollea · LeaguePoint"}</div>
        <div style={{ color: C.lime, fontSize: 24, fontWeight: 700, letterSpacing: 4 }}>vollea.co.uk</div>
      </div>
    </>
  );
}

// ---------------- PLAYER ACHIEVEMENT CARD ----------------
function PlayerAchievementBody({ player, dateLabel }) {
  const stats = [
    { label: "Rank", value: player.current_ranking ? `#${player.current_ranking}` : "—", icon: <IoTrendingUp size={36} /> },
    { label: "Points", value: player.points ?? 0, icon: <IoSparkles size={36} /> },
    { label: "Win Rate", value: `${player.win_percentage ?? 0}%`, icon: <IoFlame size={36} /> },
  ];
  const form = Array.isArray(player.recent_form) ? player.recent_form.slice(-5) : [];
  const tagline = player.highest_ranking && player.current_ranking === player.highest_ranking
    ? "Career high — flying right now."
    : player.win_percentage >= 70
      ? "On a roll."
      : "Building the streak.";

  return (
    <>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div style={{ color: C.lime, fontSize: 36, fontWeight: 700, letterSpacing: 6, textTransform: "uppercase", marginBottom: 48 }}>Player Card</div>
        <Avatar uri={player.photo} name={player.name} size={300} />
        <div style={{ color: C.text, fontSize: 84, fontWeight: 800, marginTop: 40, maxWidth: 920, lineHeight: 1.05 }}>{player.name}</div>
        <div style={{ color: C.textMuted, fontSize: 30, fontWeight: 600, marginTop: 16, textTransform: "capitalize" }}>
          {(player.skill_level || "beginner") + (player.location ? ` · ${player.location}` : "")}
        </div>
        <div style={{ color: C.lime, fontSize: 32, fontWeight: 700, marginTop: 36, fontStyle: "italic" }}>“{tagline}”</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 80, width: "100%", maxWidth: 920 }}>
          {stats.map((s) => (
            <div key={s.label} style={{
              background: C.surface, border: `2px solid ${C.lime}`,
              borderRadius: 28, padding: 32, textAlign: "center",
            }}>
              <div style={{ color: C.lime, marginBottom: 12, display: "flex", justifyContent: "center" }}>{s.icon}</div>
              <div style={{ color: C.text, fontSize: 56, fontWeight: 800, lineHeight: 1 }}>{s.value}</div>
              <div style={{ color: C.textMuted, fontSize: 22, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginTop: 10 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {form.length > 0 && (
          <div style={{ marginTop: 64 }}>
            <div style={{ color: C.textMuted, fontSize: 22, letterSpacing: 4, textTransform: "uppercase", fontWeight: 700, marginBottom: 18 }}>Recent form</div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              {form.map((r, i) => {
                const win = String(r).toUpperCase().startsWith("W");
                return (
                  <div key={i} style={{
                    width: 72, height: 72, borderRadius: 999,
                    background: win ? C.lime : C.surface3,
                    color: win ? C.onLime : C.text,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 32, letterSpacing: 2,
                    border: win ? "none" : `2px solid ${C.border}`,
                  }}>{win ? "W" : "L"}</div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "inline-flex", gap: 24, marginTop: 80 }}>
          <Stat tile label="Played" value={player.matches_played ?? 0} />
          <Stat tile label="Wins" value={player.wins ?? 0} accent />
          <Stat tile label="Losses" value={player.losses ?? 0} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 40 }}>
        <div style={{ color: C.textMuted, fontSize: 24, fontWeight: 600 }}>{dateLabel || "Vollea · LeaguePoint"}</div>
        <div style={{ color: C.lime, fontSize: 24, fontWeight: 700, letterSpacing: 4 }}>vollea.co.uk</div>
      </div>
    </>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div style={{ textAlign: "center", minWidth: 160 }}>
      <div style={{ color: accent ? C.lime : C.text, fontSize: 56, fontWeight: 800, lineHeight: 1 }}>{value}</div>
      <div style={{ color: C.textMuted, fontSize: 22, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginTop: 8 }}>{label}</div>
    </div>
  );
}

const WinnerCard = React.forwardRef(function WinnerCard(
  { variant = "league", leagueName, division, winner, runners = [], player, dateLabel },
  ref,
) {
  return (
    <div ref={ref} style={wrapStyle}>
      <div style={headerRow}>
        <img src={LOGO_URL} alt="Vollea" crossOrigin="anonymous" style={{ height: 90, width: "auto", objectFit: "contain" }} />
        <div style={cornerBadge}>
          <IoTrophy size={28} /> {variant === "league" ? "Champion" : "Player"}
        </div>
      </div>

      {variant === "league" ? (
        <LeagueWinnerBody leagueName={leagueName} division={division} winner={winner} runners={runners} dateLabel={dateLabel} />
      ) : (
        <PlayerAchievementBody player={player} dateLabel={dateLabel} />
      )}
    </div>
  );
});

export default WinnerCard;
